import { Plus } from "lucide-react";
import { PermissionGroupsView } from "@/components/features/settings/permission-groups/PermissionGroupsView";
import UsersCard from "@/components/features/settings/users/UsersCard";
import { Button, GlobalNotAuthorized, Text } from "@/components/ui";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/shadcn/Tabs/Tabs";
import { useUser } from "@/components/UserProvider";
import {
  PAGE_PERMISSION_KEYS,
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/_auth/settings/users")({
  component: RouteComponent,
});

function RouteComponent() {
  const { hasPagePermission, hasActionPermission } = useUser();
  const canViewUsers = hasPagePermission(
    PAGE_PERMISSION_KEYS.firmSettingsUsers,
  );
  const canViewPermissionGroups = canViewUsers;
  const canInviteUsers = hasActionPermission(
    ACTION_PERMISSION_KEYS.user,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdateUsers = hasActionPermission(
    ACTION_PERMISSION_KEYS.user,
    ACTION_PERMISSION_VALUES.update,
  );
  const canCreatePermissionGroup = hasActionPermission(
    ACTION_PERMISSION_KEYS.permissionGroup,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdatePermissionGroup = hasActionPermission(
    ACTION_PERMISSION_KEYS.permissionGroup,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDeletePermissionGroup = hasActionPermission(
    ACTION_PERMISSION_KEYS.permissionGroup,
    ACTION_PERMISSION_VALUES.delete,
  );

  const defaultTab = canViewUsers ? "users" : "permission-groups";
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [isInvitingUser, setIsInvitingUser] = useState(false);
  const [isReassigningUser, setIsReassigningUser] = useState(false);
  const [isAddingPermissionGroup, setIsAddingPermissionGroup] = useState(false);

  if (!canViewUsers && !canViewPermissionGroups) {
    return <GlobalNotAuthorized />;
  }

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <Text as="h1" variant="title" className="mb-6 shrink-0">
        Users
      </Text>
      <div className="mb-2 flex min-h-14 w-full items-end justify-between gap-4 border-b">
        <TabsList className="h-auto flex-1 justify-start gap-8 rounded-none border-0 bg-transparent p-0">
          <TabsTrigger
            value="users"
            className="rounded-none px-0 pb-4 data-[state=active]:bg-transparent"
          >
            Users
          </TabsTrigger>
          {canViewPermissionGroups ? (
            <TabsTrigger
              value="permission-groups"
              className="rounded-none px-0 pb-4 data-[state=active]:bg-transparent"
            >
              Permission groups
            </TabsTrigger>
          ) : null}
        </TabsList>
        <div className="flex shrink-0 flex-wrap items-center gap-2 pb-4">
          {activeTab === "users" && canUpdateUsers ? (
            <Button
              variant="outline"
              size="md"
              onClick={() => {
                setIsReassigningUser(true);
              }}
            >
              Reassign user
            </Button>
          ) : null}
          {activeTab === "users" && canInviteUsers ? (
            <Button
              variant="foreground"
              size="md"
              onClick={() => {
                setIsInvitingUser(true);
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Invite user
            </Button>
          ) : null}
          {activeTab === "permission-groups" && canCreatePermissionGroup ? (
            <Button
              variant="foreground"
              size="md"
              onClick={() => {
                setIsAddingPermissionGroup(true);
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add permission group
            </Button>
          ) : null}
        </div>
      </div>
      <TabsContent value="users" className="mt-0">
        <UsersCard
          isInvitingUser={isInvitingUser}
          setIsInvitingUser={setIsInvitingUser}
          isReassigningUser={isReassigningUser}
          setIsReassigningUser={setIsReassigningUser}
        />
      </TabsContent>
      {canViewPermissionGroups ? (
        <TabsContent value="permission-groups" className="mt-0">
          <PermissionGroupsView
            canUpdate={canUpdatePermissionGroup}
            canDelete={canDeletePermissionGroup}
            isAdding={isAddingPermissionGroup}
            setIsAdding={setIsAddingPermissionGroup}
          />
        </TabsContent>
      ) : null}
    </Tabs>
  );
}
